import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { addForklift, updateForklift } from "../../views/Fleet/fleetApi";

/* ------------------------------------------------------------------
   Forklift dialogs shared by Forklifts, a forklift's page, and a
   location's page in Location Manager.
------------------------------------------------------------------ */

export const ErrorLine = ({ children }) =>
  children ? (
    <p role="alert" className="mt-3 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
      {children}
    </p>
  ) : null;

export const AddForkliftDialog = ({ open, onClose, onAdded, locations, userId, defaultBranch }) => {
  const [unit, setUnit] = useState("");
  const [location, setLocation] = useState("");
  const [moffett, setMoffett] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setUnit("");
      setLocation(defaultBranch || "");
      setMoffett(false);
      setError(null);
    }
  }, [open, defaultBranch]);

  const canSave = unit.trim() && location && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const { id } = await addForklift(userId, { unit: unit.trim(), location, moffett });
      onAdded(id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }} aria-labelledby="add-forklift-title">
      <div className="p-5 sm:p-6">
        <h2 id="add-forklift-title" className="text-lg font-semibold">
          Add forklift
        </h2>
        <p className="app-muted mt-1 text-sm">
          New units start in service and show up on the branch kiosk and in the mobile app right away.
        </p>
        <div className="mt-5 space-y-4">
          <TextField
            fullWidth
            size="small"
            label="Unit number"
            value={unit}
            autoFocus
            onChange={(e) => setUnit(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
          />
          <FormControl fullWidth size="small">
            <InputLabel id="add-forklift-branch">Branch</InputLabel>
            <Select
              labelId="add-forklift-branch"
              label="Branch"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            >
              {locations.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={moffett} onChange={(e) => setMoffett(e.target.checked)} />}
            label="This unit is a Moffett"
          />
        </div>
        <ErrorLine>{error}</ErrorLine>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={!canSave}>
            {saving ? "Adding…" : "Add forklift"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export const EditForkliftDialog = ({ forklift, open, onClose, onSaved, locations, userId }) => {
  const [unit, setUnit] = useState("");
  const [location, setLocation] = useState("");
  const [moffett, setMoffett] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && forklift) {
      setUnit(forklift.unit);
      setLocation(forklift.location);
      setMoffett(forklift.moffett);
      setError(null);
    }
  }, [open, forklift]);

  const options = useMemo(() => {
    const names = new Set(locations);
    if (forklift?.location) names.add(forklift.location);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [locations, forklift]);

  const save = async () => {
    if (!unit.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await updateForklift(userId, forklift.id, { unit: unit.trim(), location, moffett });
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }} aria-labelledby="edit-forklift-title">
      <div className="p-5 sm:p-6">
        <h2 id="edit-forklift-title" className="text-lg font-semibold">
          Edit unit #{forklift?.unit}
        </h2>
        <p className="app-muted mt-1 text-sm">
          Renaming or moving a unit keeps its check-in history with it.
        </p>
        <div className="mt-5 space-y-4">
          <TextField fullWidth size="small" label="Unit number" value={unit} onChange={(e) => setUnit(e.target.value)} />
          <FormControl fullWidth size="small">
            <InputLabel id="edit-forklift-branch">Branch</InputLabel>
            <Select labelId="edit-forklift-branch" label="Branch" value={location} onChange={(e) => setLocation(e.target.value)}>
              {options.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={moffett} onChange={(e) => setMoffett(e.target.checked)} />}
            label="This unit is a Moffett"
          />
        </div>
        <ErrorLine>{error}</ErrorLine>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={!unit.trim() || saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

/** Yes/no confirmation. `children` render between the body and the buttons. */
export const ConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel,
  danger,
  busy,
  disabled,
  error,
  onConfirm,
  onClose,
  children,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }} aria-labelledby="fleet-confirm-title">
    <div className="p-5 sm:p-6">
      <h2 id="fleet-confirm-title" className="text-lg font-semibold">
        {title}
      </h2>
      <p className="app-muted mt-2 text-sm">{body}</p>
      {children}
      <ErrorLine>{error}</ErrorLine>
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button onClick={onClose} color="inherit" disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color={danger ? "error" : "primary"} onClick={onConfirm} disabled={busy || disabled}>
          {busy ? "Working…" : confirmLabel}
        </Button>
      </div>
    </div>
  </Dialog>
);
